if (!customElements.get('vince-gallery')) {
  customElements.define('vince-gallery', class extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.slides = this.querySelector('[data-slides]');
      this.items = [...this.querySelectorAll('[data-open]')];
      this.thumbnails = [...this.querySelectorAll('[data-index]')];
      this.dialog = this.querySelector('dialog');
      this.viewport = this.querySelector('[data-modal-viewport]');
      this.modalImage = this.querySelector('[data-modal-image]');
      this.index = 0;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.querySelector('[data-controls]').hidden = false;
      this.querySelector('[data-thumbnails]').hidden = false;
      this.addEventListener('click', this.onClick.bind(this), options);
      this.slides.addEventListener('scroll', () => {
        cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = requestAnimationFrame(() => {
          const next = Math.round(this.slides.scrollLeft / this.slides.clientWidth);
          if (Number.isFinite(next) && next !== this.index) this.setIndex(next);
        });
      }, { ...options, passive: true });
      this.dialog.addEventListener('close', () => this.closeModalState(), options);
      this.dialog.addEventListener('keydown', (event) => {
        if (this.viewport.classList.contains('is-zoomed')) return;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          this.goTo(this.index + (event.key === 'ArrowLeft' ? -1 : 1), false);
          this.renderModal();
        }
      }, options);
      this.viewport.addEventListener('pointerdown', (event) => {
        this.pointerStart = { x: event.clientX, y: event.clientY, type: event.pointerType };
      }, options);
      this.viewport.addEventListener('pointerup', (event) => {
        if (!this.pointerStart || this.pointerStart.type !== 'touch' || this.viewport.classList.contains('is-zoomed')) return;
        const dx = event.clientX - this.pointerStart.x;
        const dy = event.clientY - this.pointerStart.y;
        this.pointerStart = null;
        if (Math.abs(dx) < 45 || Math.abs(dx) < Math.abs(dy)) return;
        this.suppressZoomUntil = performance.now() + 400;
        this.goTo(this.index + (dx < 0 ? 1 : -1), false);
        this.renderModal();
      }, options);
      this.resizeObserver = new ResizeObserver(() => {
        this.slides.scrollTo({ left: this.index * this.slides.clientWidth, behavior: 'instant' });
      });
      this.resizeObserver.observe(this.slides);
      this.setIndex(0, false);
    }

    disconnectedCallback() {
      if (this.dialog?.open) this.dialog.close();
      this.closeModalState(false);
      this.events?.abort();
      this.events = null;
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.scrollFrame);
    }

    onClick(event) {
      const button = event.target.closest('button');
      if (button && this.contains(button)) {
        if (button.hasAttribute('data-open')) {
          this.goTo(Number(button.dataset.open), false);
          this.returnFocus = button;
          this.renderModal();
          this.previousBodyOverflow = document.body.style.overflow;
          document.body.style.overflow = 'hidden';
          this.dialog.showModal();
        } else if (button.hasAttribute('data-index')) {
          this.goTo(Number(button.dataset.index));
        } else if (button.hasAttribute('data-step')) {
          this.goTo(this.index + Number(button.dataset.step));
        } else if (button.hasAttribute('data-modal-step')) {
          this.goTo(this.index + Number(button.dataset.modalStep), false);
          this.renderModal();
        } else if (button.hasAttribute('data-close')) {
          this.dialog.close();
        } else if (button.hasAttribute('data-zoom')) {
          this.toggleZoom();
        }
      } else if (event.target === this.modalImage && performance.now() >= (this.suppressZoomUntil || 0)) {
        this.toggleZoom();
      } else if (event.target === this.dialog) {
        const bounds = this.dialog.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) this.dialog.close();
      }
    }

    goTo(index, smooth = true) {
      const next = Math.max(0, Math.min(this.items.length - 1, index));
      this.setIndex(next);
      this.slides.scrollTo({
        left: next * this.slides.clientWidth,
        behavior: smooth && !this.reducedMotion.matches ? 'smooth' : 'instant',
      });
    }

    setIndex(index, announce = true) {
      this.index = Math.max(0, Math.min(this.items.length - 1, index));
      this.querySelector('[data-counter]').textContent = String(this.index + 1);
      this.items.forEach((item, position) => { item.tabIndex = position === this.index ? 0 : -1; });
      this.thumbnails.forEach((thumbnail, position) => {
        if (position === this.index) thumbnail.setAttribute('aria-current', 'true');
        else thumbnail.removeAttribute('aria-current');
      });
      for (const button of this.querySelectorAll('[data-step], [data-modal-step]')) {
        const step = Number(button.dataset.step || button.dataset.modalStep);
        button.disabled = step < 0 ? this.index === 0 : this.index === this.items.length - 1;
      }
      const thumbnail = this.thumbnails[this.index];
      const strip = this.querySelector('[data-thumbnails]');
      if (thumbnail && strip) {
        const left = thumbnail.offsetLeft - strip.offsetLeft;
        if (left < strip.scrollLeft || left + thumbnail.offsetWidth > strip.scrollLeft + strip.clientWidth) {
          strip.scrollTo({ left: Math.max(0, left - (strip.clientWidth - thumbnail.offsetWidth) / 2), behavior: 'instant' });
        }
      }
      if (announce && window.accessibilityStrings?.imageAvailable) {
        this.querySelector('[data-status]').textContent = window.accessibilityStrings.imageAvailable.replace('[index]', String(this.index + 1));
      }
    }

    renderModal() {
      const item = this.items[this.index];
      this.viewport.classList.remove('is-zoomed');
      this.viewport.scrollTo(0, 0);
      this.querySelector('[data-zoom]').setAttribute('aria-pressed', 'false');
      this.modalImage.src = item.dataset.fullSrc;
      this.modalImage.alt = item.querySelector('img').alt;
      this.querySelector('[data-modal-counter]').textContent = `${this.index + 1} / ${this.items.length}`;
    }

    toggleZoom() {
      const zoomed = this.viewport.classList.toggle('is-zoomed');
      this.querySelector('[data-zoom]').setAttribute('aria-pressed', String(zoomed));
      this.viewport.scrollTo(0, 0);
    }

    closeModalState(restoreFocus = true) {
      if (this.previousBodyOverflow !== undefined) {
        document.body.style.overflow = this.previousBodyOverflow;
        this.previousBodyOverflow = undefined;
      }
      if (restoreFocus && this.returnFocus?.isConnected) this.returnFocus.focus({ preventScroll: true });
      this.returnFocus = null;
    }
  });
}
